
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Berlin TOC mapping service
 *
 * @file BerlinTocMappingService.js
 * @author Satyaki Bose
 */

import Framework, { BaseService } from '@greenville/framework';
import { AET_STORE_EVENTS, TOC_MAPPING_RESPONSE_SOURCE } from '../common/Constants';
import normalizeTocMappingResponse from '../utils/normalizeTocMappingResponse';
import * as GraphClient from '../utils/GraphClient';

export default class BerlinTocMappingService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.BERLIN_TOC_MAPPING_REQUESTED, 'berlinTocMappingStatus');
    this.streamAbortController = null;
  }

  abortStream() {
    if (this.streamAbortController) {
      this.streamAbortController.abort();
      this.streamAbortController = null;
    }
  }

  handleEvent(eventName, requestPayload = {}) {
    const { graphConfig, ...graphPayload } = requestPayload;

    let resolvedGraphConfig;
    try {
      resolvedGraphConfig = GraphClient.resolveGraphConfig(graphConfig);
    } catch (error) {
      return Promise.reject(error);
    }

    this.abortStream();

    let signal;
    if (resolvedGraphConfig.useStream) {
      this.streamAbortController = new AbortController();
      signal = this.streamAbortController.signal;
    }

    return GraphClient.runGraph({
      client: this.getClient('berlin'),
      graphConfig: resolvedGraphConfig,
      payload: graphPayload,
      signal
    });
  }

  handleResponse(response) {
    this.abortStream();
    try {
      const normalized = normalizeTocMappingResponse(response, TOC_MAPPING_RESPONSE_SOURCE.BERLIN);
      Framework.getStoreRegistry()
        .getStore('prismTocMapping')
        .setTocMapping(normalized);
      Framework.getEventManager().publish(AET_STORE_EVENTS.TOC_MAPPING_COMPLETED, {
        ...response,
        data: normalized
      });
    } catch (error) {
      console.error('Error handling Berlin TOC mapping response:', error);
      Framework.getEventManager().publish(AET_STORE_EVENTS.TOC_MAPPING_FAILED, {
        error: error?.message || String(error)
      });
    }
  }

  handleError(error) {
    this.abortStream();
    console.error('Berlin TOC mapping service failed:', error);
    Framework.getEventManager().publish(AET_STORE_EVENTS.TOC_MAPPING_FAILED, {
      error: error?.message || String(error)
    });
  }
}
